import { createEdge, createNode } from '../../core/defaults';
import type { DiagramEdge, DiagramNode } from '../../core/types';

export interface OrgCsvResult {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  warnings: string[];
  rowCount: number;
}

function normalizeHeader(value: string): string {
  return value.trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
}

function normalizeName(value: string): string {
  return value.trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const candidates = [',', ';', '\t'];
  return candidates.sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0];
}

export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') { field += '"'; index += 1; }
      else quoted = !quoted;
      continue;
    }
    if (!quoted && char === delimiter) { row.push(field.trim()); field = ''; continue; }
    if (!quoted && (char === '\n' || char === '\r')) {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(field.trim()); field = '';
      if (row.some((value) => value.length > 0)) rows.push(row);
      row = [];
      continue;
    }
    field += char;
  }
  row.push(field.trim());
  if (row.some((value) => value.length > 0)) rows.push(row);
  return rows;
}

function findColumn(headers: string[], aliases: string[]): number {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((header) => aliases.includes(header));
}

export function importOrgCsv(text: string): OrgCsvResult {
  if (text.length > 5_000_000) throw new Error('O CSV excede o limite local de 5 MB.');
  const rows = parseCsv(text.replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('O CSV precisa conter cabeçalho e pelo menos uma linha de dados.');
  const headers = rows[0];
  const nameIndex = findColumn(headers, ['nome', 'name', 'funcionario', 'colaborador', 'employee', 'pessoa']);
  const roleIndex = findColumn(headers, ['cargo', 'role', 'funcao', 'position', 'titulo']);
  const managerIndex = findColumn(headers, ['gestor', 'manager', 'superior', 'lider', 'chefia', 'reportato']);
  const departmentIndex = findColumn(headers, ['departamento', 'department', 'area', 'setor']);
  if (nameIndex < 0) throw new Error('Não encontrei a coluna de nome. Use um cabeçalho como “Nome”.');

  const warnings: string[] = [];
  const sourceRows = rows.slice(1, 2001).map((columns, index) => ({
    line: index + 2,
    name: (columns[nameIndex] ?? '').trim(),
    role: roleIndex >= 0 ? (columns[roleIndex] ?? '').trim() : '',
    manager: managerIndex >= 0 ? (columns[managerIndex] ?? '').trim() : '',
    department: departmentIndex >= 0 ? (columns[departmentIndex] ?? '').trim() : '',
  })).filter((row) => row.name);
  if (rows.length - 1 > 2000) warnings.push('A importação foi limitada às primeiras 2.000 pessoas para preservar a responsividade do navegador.');
  if (sourceRows.length === 0) throw new Error('Nenhuma pessoa com nome foi encontrada no CSV.');

  const nodes: DiagramNode[] = [];
  const nodeByName = new Map<string, DiagramNode>();
  const duplicateNames = new Set<string>();
  for (const row of sourceRows) {
    const key = normalizeName(row.name);
    if (nodeByName.has(key)) { duplicateNames.add(key); warnings.push(`Linha ${row.line}: nome duplicado “${row.name}”; a primeira ocorrência será usada nas relações.`); }
    const node = createNode('orgPerson');
    node.text = row.name;
    node.data = { name: row.name, role: row.role, department: row.department };
    nodes.push(node);
    if (!nodeByName.has(key)) nodeByName.set(key, node);
  }

  const edges: DiagramEdge[] = [];
  sourceRows.forEach((row, index) => {
    if (!row.manager) return;
    const child = nodes[index];
    const managerKey = normalizeName(row.manager);
    const manager = nodeByName.get(managerKey);
    if (!manager) { warnings.push(`Linha ${row.line}: gestor “${row.manager}” não foi encontrado no arquivo.`); return; }
    if (manager.id === child.id) { warnings.push(`Linha ${row.line}: a pessoa não pode ser gestora de si mesma.`); return; }
    if (duplicateNames.has(managerKey)) warnings.push(`Linha ${row.line}: gestor “${row.manager}” é ambíguo porque há nomes duplicados.`);
    const edge = createEdge(manager.id, child.id);
    edge.sourceAnchor = 'bottom';
    edge.targetAnchor = 'top';
    edges.push(edge);
  });

  return { nodes, edges, warnings: Array.from(new Set(warnings)).slice(0, 100), rowCount: sourceRows.length };
}
